// Field-label dictionary used by the detail view.
//
// Hand-curated for the demo. A future iteration can replace this with a
// backend-driven metadata endpoint per (jurisdiction, entity_type, source)
// when the catalogue grows past a few dozen keys.

import type { EntitySubtype } from './api';

export const FIELD_LABELS: Record<string, string> = {
  // Addresses
  city: 'City',
  country: 'Country',
  postal_code: 'Postal code',
  street: 'Street',
  region: 'Region',

  // CEIDG / person roles
  first_name: 'First name',
  last_name: 'Last name',
  trade_name: 'Trade name',
  nip: 'NIP',
  regon: 'REGON',
  status: 'Status',
  status_flags: 'Status flags',
  source_url: 'Source URL',

  // Handelsregister
  register_court: 'Register court',
  register_number: 'Register number',
  description: 'Description',
};

// Tokens that should render fully upper-cased rather than title-cased, so
// "Verified by Gleif" → "GLEIF" and "Vat Number" → "VAT Number".
const ACRONYMS = new Set([
  'vat',
  'lei',
  'nip',
  'regon',
  'gleif',
  'vies',
  'krs',
  'cvr',
  'id',
  'url',
]);

function applyAcronyms(label: string): string {
  return label
    .split(' ')
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w))
    .join(' ');
}

export function labelFor(key: string): string {
  const explicit = FIELD_LABELS[key];
  if (explicit) return explicit;
  const humanised = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return applyAcronyms(humanised);
}

// Structural entity archetype (EntityArchetype) → human label. Surfaces the
// legal-form ontology in the UI (e.g. a GmbH shows "Private limited company").
export const ARCHETYPE_LABELS: Record<string, string> = {
  LTD_CO: 'Private limited company',
  STOCK_CORP: 'Stock corporation',
  LISTED_CO: 'Listed company',
  PARTNERSHIP: 'Partnership',
  SOLE_PROP: 'Sole proprietorship',
  COOPERATIVE: 'Cooperative',
  ASSOCIATION: 'Association',
  FOUNDATION: 'Foundation',
  PUBLIC_BODY: 'Public body',
  LEGAL_ARRANGEMENT: 'Legal arrangement',
  BRANCH: 'Branch',
  OTHER: 'Other',
};

export function archetypeLabel(code: string): string {
  return ARCHETYPE_LABELS[code] ?? code;
}

// BODS-aligned ownership/control interest types → human label.
export const INTEREST_TYPE_LABELS: Record<string, string> = {
  shareholding: 'Shareholding',
  votingRights: 'Voting rights',
  appointmentOfBoard: 'Board appointment rights',
  seniorManagingOfficial: 'Senior managing official',
  controlViaCompanyRulesOrArticles: 'Control via articles',
  otherInfluenceOrControl: 'Other influence or control',
  accountingConsolidationParent: 'Accounting consolidation parent',
  subsidiary: 'Subsidiary',
  unknown: 'Ownership / control',
};

export function interestTypeLabel(code: string): string {
  return INTEREST_TYPE_LABELS[code] ?? labelFor(code);
}

// GLEIF / BO-register reporting-exception categories → human label. These mean
// "the entity reports it has no parent", a terminal answer (not "not found").
export const REPORTING_EXCEPTION_LABELS: Record<string, string> = {
  NON_CONSOLIDATING: 'No consolidating parent reported',
  NON_PUBLIC: 'Parent exists but is not public',
  NO_LEI: 'Parent has no LEI',
  NO_KNOWN_PERSON: 'No known controlling person',
  BINDING_LEGAL_COMMITMENTS: 'Disclosure barred by legal commitments',
  LEGAL_OBSTACLES: 'Disclosure barred by legal obstacles',
  DETRIMENT_NOT_EXCLUDED: 'Disclosure could be detrimental',
  DISCLOSURE_DETRIMENTAL: 'Disclosure detrimental',
};

export function reportingExceptionLabel(code: string): string {
  return REPORTING_EXCEPTION_LABELS[code] ?? labelFor(code);
}

export const ENTITY_SUBTYPE_LABELS: Record<EntitySubtype, string> = {
  LEGAL_ENTITY: 'Legal entity',
  NATURAL_PERSON: 'Natural person',
  SOLE_PROPRIETOR: 'Sole proprietor',
  OFFICER: 'Officer',
};

// Friendly source labels — populated at app startup from the backend's
// /sources endpoint so backend/src/searchx/config/sources.yaml stays the
// single source of truth. Falls back to a humanised slug for any source
// that isn't in the map yet (e.g. before the API call resolves).
const _sourceLabels: Map<string, string> = new Map();

export function setSourceLabels(entries: Iterable<[string, string]>): void {
  _sourceLabels.clear();
  for (const [name, label] of entries) {
    _sourceLabels.set(name, label);
  }
}

export function sourceLabel(name: string): string {
  const fromMap = _sourceLabels.get(name);
  if (fromMap !== undefined && fromMap.length > 0) {
    return fromMap;
  }
  return applyAcronyms(name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
}
